import React, { useState, useMemo } from "react";
import {
  USERS_PAGE_TITLE,
  FILTER_PLACEHOLDER,
  ORGANIZATION_FILTER_LABEL,
} from "./users.constants";
import { useUsers } from "./hooks";
import {
  EmptyState,
  UsersTable,
  AddUserModal,
  EditUserModal,
} from "./components";
import LoadingDots from "@/shared/components/LoadingDots";
import { Alert } from "@/shared/components/ui/alert";
import { User, UserService } from "@/shared/services/user.service";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { toast } from "sonner";
import { Input } from "@/shared/components/ui/input";
import { Select } from "@/shared/components/ui/select";

/**
 * Users page component
 * Displays a list of all users with filtering capability
 */
const Users: React.FC = () => {
  const { users, loading, error, refetch } = useUsers();
  const [filterQuery, setFilterQuery] = useState("");
  const [selectedOrganization, setSelectedOrganization] =
    useState<string>("all");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  // Get unique organizations for filter dropdown
  const organizations = useMemo(() => {
    if (!users || users.length === 0) return [];

    const orgs = users
      .filter((user) => user.organization)
      .map((user) => ({
        id: user.organization?.id || "",
        name: user.organization?.name || "",
      }));

    // Remove duplicates using Map to keep only unique organization IDs
    return Array.from(new Map(orgs.map((org) => [org.id, org])).values());
  }, [users]);

  // Filter users based on search query and selected organization
  const filteredUsers = useMemo(() => {
    if (!users) return [];

    let filtered = [...users];

    // Filter by organization
    if (selectedOrganization !== "all") {
      filtered = filtered.filter(
        (user) =>
          user.organization && user.organization.id === selectedOrganization
      );
    }

    // Filter by text query
    if (filterQuery && filterQuery.trim()) {
      const lowercaseQuery = filterQuery.toLowerCase();
      filtered = filtered.filter(
        (user) =>
          (user.name && user.name.toLowerCase().includes(lowercaseQuery)) ||
          (user.email && user.email.toLowerCase().includes(lowercaseQuery)) ||
          (user.role && user.role.toLowerCase().includes(lowercaseQuery)) ||
          (user.organization?.name &&
            user.organization.name.toLowerCase().includes(lowercaseQuery))
      );
    }

    return filtered;
  }, [users, filterQuery, selectedOrganization]);

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingUser(null);
  };

  const handleDeleteUser = (user: User) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleActivateUser = async (user: User) => {
    try {
      await UserService.activate(user.id);
      toast.success(`User "${user.name}" activated successfully`);
      refetch();
    } catch (error) {
      console.error("Failed to activate user:", error);
      toast.error("Failed to activate user");
    }
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;

    try {
      await UserService.delete(userToDelete.id);
      toast.success("User deleted successfully");
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      refetch();
    } catch (error) {
      console.error("Failed to delete user:", error);
      toast.error("Failed to delete user");
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">{USERS_PAGE_TITLE}</h1>
        <AddUserModal onUserAdded={refetch} />
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-col gap-4">
        <Input
          placeholder={FILTER_PLACEHOLDER}
          value={filterQuery}
          onChange={(e) => setFilterQuery(e.target.value)}
          className="max-w-md"
        />

        <div className="flex items-center gap-2">
          <label htmlFor="organization-filter" className="text-sm font-medium">
            {ORGANIZATION_FILTER_LABEL}
          </label>
          <Select
            id="organization-filter"
            value={selectedOrganization}
            onChange={(e) => setSelectedOrganization(e.target.value)}
            className="w-[200px]"
          >
            <option value="all">All Organizations</option>
            {organizations.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {loading ? (
        <LoadingDots />
      ) : error ? (
        <Alert variant="destructive">{error}</Alert>
      ) : filteredUsers.length === 0 ? (
        <EmptyState />
      ) : (
        <UsersTable
          users={filteredUsers}
          onEditUser={handleEditUser}
          onDeleteUser={handleDeleteUser}
          onActivateUser={handleActivateUser}
        />
      )}

      <EditUserModal
        user={editingUser}
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        onUserUpdated={refetch}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the user "{userToDelete?.name}"?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;
